import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { FileUpload } from '../components/FileUpload';
import { prestacoesApi } from '../api/backend';
import { useAuthStore } from '../store/authStore';
import { DocumentoFiscal } from '../types';

export function NovoDocumentoPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filePath, setFilePath] = useState<string>('');
  
  const [formData, setFormData] = useState({
    tipo_lancamento: 'rateio',
    tipo_documento: 'nota_fiscal',
    fornecedor_nome: '',
    fornecedor_documento: '',
    data_emissao: '',
    data_lancamento: '',
    mes_lancamento: new Date().getMonth() + 1,
    numero_documento: '',
    natureza_documento: '',
    rubrica_documento: '',
    valor_original: '',
    valor_liquido: '',
    historico: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      setError('Usuário não autenticado');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Validações básicas
      if (!formData.fornecedor_nome || !formData.fornecedor_documento || !formData.numero_documento) {
        throw new Error('Preencha todos os campos obrigatórios');
      }

      const data = {
        ...formData,
        tipo_lancamento: 'rateio',
        mes_lancamento: parseInt(formData.mes_lancamento as any),
        valor_original: parseFloat(formData.valor_original),
        valor_liquido: parseFloat(formData.valor_liquido),
        caminho_arquivo_nota_fiscal: filePath || null,
        caminho_arquivo_comprovante: filePath || null,
      };

      await prestacoesApi.addDocumento(Number(id), data);
      
      navigate(`/prestacoes/${id}`);
    } catch (err: any) {
      console.error('Error saving document:', err);
      setError(err.message || 'Erro ao salvar documento');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link
          to={`/prestacoes/${id}`}
          className="inline-flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Voltar</span>
        </Link>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Adicionar Documento Fiscal
          </h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Tipo de Documento */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Tipo de Documento *
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, tipo_documento: 'nota_fiscal' }))}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    formData.tipo_documento === 'nota_fiscal'
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                  }`}
                >
                  <div className="text-left">
                    <div className="font-semibold text-gray-900 dark:text-white">Nota Fiscal</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Documento fiscal obrigatório para todos os lançamentos
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, tipo_documento: 'comprovante_pagamento' }))}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    formData.tipo_documento === 'comprovante_pagamento'
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                  }`}
                >
                  <div className="text-left">
                    <div className="font-semibold text-gray-900 dark:text-white">Comprovante de Pagamento</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Documento complementar opcional
                    </div>
                  </div>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nome Fantasia *
                </label>
                <input
                  type="text"
                  name="fornecedor_nome"
                  value={formData.fornecedor_nome}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  CNPJ *
                </label>
                <input
                  type="text"
                  name="fornecedor_documento"
                  value={formData.fornecedor_documento}
                  onChange={handleChange}
                  required
                  placeholder="00.000.000/0000-00"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Data de Emissão *
                </label>
                <input
                  type="date"
                  name="data_emissao"
                  value={formData.data_emissao}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Data de Lançamento *
                </label>
                <input
                  type="date"
                  name="data_lancamento"
                  value={formData.data_lancamento}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Mês de Lançamento *
                </label>
                <select
                  name="mes_lancamento"
                  value={formData.mes_lancamento}
                  onChange={handleChange as any}
                  required
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((mes) => (
                    <option key={mes} value={mes}>
                      {new Date(2000, mes - 1).toLocaleString('pt-BR', { month: 'long' })}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Número do Documento *
                </label>
                <input
                  type="text"
                  name="numero_documento"
                  value={formData.numero_documento}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Natureza
                </label>
                <input
                  type="text"
                  name="natureza_documento"
                  value={formData.natureza_documento}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Rubrica
                </label>
                <input
                  type="text"
                  name="rubrica_documento"
                  value={formData.rubrica_documento}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Valor Original *
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="valor_original"
                  value={formData.valor_original}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Valor Líquido *
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="valor_liquido"
                  value={formData.valor_liquido}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Histórico
              </label>
              <textarea
                name="historico"
                value={formData.historico}
                onChange={handleChange}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <FileUpload
              onFileUploaded={setFilePath}
              accept=".pdf"
              maxSize={10}
              label="Nota Fiscal (PDF)"
            />

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
              </div>
            )}

            <div className="flex justify-end space-x-4">
              <Link
                to={`/prestacoes/${id}`}
                className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancelar
              </Link>
              <button
                type="submit"
                disabled={loading}
                className="flex items-center space-x-2 px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-5 h-5" />
                <span>{loading ? 'Salvando...' : 'Salvar Documento'}</span>
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
